import VirtualTabletApi, {
  type VirtualTabletAction,
  type VirtualTabletLocationInput,
} from 'clients/VirtualTabletApi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useLocale from 'src/hooks/useLocale';
import {
  GeolocationError,
  type GeolocationFailureReason,
  getUserCoordinates,
} from 'utils/GeolocationUtil';

export type VirtualTabletStep =
  'keypad' | 'acquiring-location' | 'verifying' | 'location-unavailable' | 'success';

export const PIN_LENGTH = 4;

// GPS fix timeout before offering the store-number fallback.
const GEOLOCATION_TIMEOUT_MS = 10_000;

// How long the success screen stays up before auto-resetting for the next person.
const SUCCESS_DISPLAY_MS = 10_000;

export function useVirtualTabletFlow(siteId: string) {
  const locale = useLocale();
  const genericErrorMessage = locale.virtualTablet.genericError;
  const [step, setStep] = useState<VirtualTabletStep>('keypad');
  const [storeNumber, setStoreNumber] = useState('');
  const [digits, setDigits] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Mirrors `digits`'s lifecycle — survives a GPS retry/store-number detour, cleared on success/reset.
  const [pendingAction, setPendingAction] = useState<VirtualTabletAction | null>(null);
  // Set once on success, after pendingAction is already cleared — what the success screen reads.
  const [completedAction, setCompletedAction] = useState<VirtualTabletAction | null>(null);
  // An explicit denial can never be re-prompted by any client code; timeout/unavailable can retry.
  const [locationFailureReason, setLocationFailureReason] =
    useState<GeolocationFailureReason | null>(null);
  // Absolute target timestamp, not a countdown value — survives tab backgrounding.
  const [armingUntil, setArmingUntil] = useState<string | null>(null);
  const [armingTotalSeconds, setArmingTotalSeconds] = useState(0);

  const cancelledRef = useRef(false);
  useEffect(
    () => () => {
      cancelledRef.current = true;
    },
    [],
  );

  // Ticks only while arming is in progress — reflects the server's timestamp, not an invented timer.
  useEffect(() => {
    if (!armingUntil) return undefined;
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [armingUntil]);

  const armingSecondsRemaining = useMemo(() => {
    if (!armingUntil) return 0;
    return Math.max(0, Math.ceil((new Date(armingUntil).getTime() - now) / 1000));
  }, [armingUntil, now]);

  // Auto-resets to keypad after success, waiting out any arming delay first.
  useEffect(() => {
    if (step !== 'success') return undefined;
    const resetAtMs = armingUntil
      ? new Date(armingUntil).getTime() + SUCCESS_DISPLAY_MS
      : Date.now() + SUCCESS_DISPLAY_MS;
    const timeout = setTimeout(
      () => {
        setCompletedAction(null);
        setArmingUntil(null);
        setArmingTotalSeconds(0);
        setStep('keypad');
      },
      Math.max(0, resetAtMs - Date.now()),
    );
    return () => clearTimeout(timeout);
  }, [step, armingUntil]);

  // PIN + action survive a GPS failure/retry/store-number detour.
  const submitPin = useCallback(
    async (pin: string, action: VirtualTabletAction, location: VirtualTabletLocationInput) => {
      setStep('verifying');
      const result = await VirtualTabletApi.validate(siteId, action, pin, location);
      if (cancelledRef.current) return;

      if (result.ok) {
        setDigits('');
        setPendingAction(null);
        setErrorMessage(null);
        setCompletedAction(action);
        const delaySeconds = action === 'arm' ? result.data.delaySeconds : undefined;
        if (delaySeconds && Number.isFinite(delaySeconds) && delaySeconds > 0) {
          const armedAt = Date.now();
          setNow(armedAt);
          setArmingUntil(new Date(armedAt + delaySeconds * 1000).toISOString());
          setArmingTotalSeconds(delaySeconds);
        } else {
          setArmingUntil(null);
          setArmingTotalSeconds(0);
        }
        setStep('success');
        return;
      }

      // Full reset on any failure — never reveal which part failed.
      setDigits('');
      setPendingAction(null);
      switch (result.error.error) {
        case 'no_match':
          setErrorMessage(locale.virtualTablet.noMatchMessage);
          break;
        default:
          setErrorMessage(genericErrorMessage);
      }
      setStep('keypad');
    },
    [siteId, locale, genericErrorMessage],
  );

  // GPS is a UX confirmation factor only, not a security control — server re-checks it.
  const resolveLocationAndSubmit = useCallback(
    async (pin: string, action: VirtualTabletAction) => {
      setStep('acquiring-location');
      setLocationFailureReason(null);
      try {
        const coords = await getUserCoordinates({ timeout: GEOLOCATION_TIMEOUT_MS });
        if (cancelledRef.current) return;
        const location: VirtualTabletLocationInput = {
          type: 'gps',
          lat: coords.latitude,
          lng: coords.longitude,
        };
        await submitPin(pin, action, location);
      } catch (err) {
        if (!cancelledRef.current) {
          setLocationFailureReason(err instanceof GeolocationError ? err.reason : 'unavailable');
          setStep('location-unavailable');
        }
      }
    },
    [submitPin],
  );

  const retryLocation = useCallback(() => {
    if (!pendingAction) return;
    void resolveLocationAndSubmit(digits, pendingAction);
  }, [digits, pendingAction, resolveLocationAndSubmit]);

  const submitStoreNumber = useCallback(() => {
    if (!storeNumber.trim() || !pendingAction) return;
    const location: VirtualTabletLocationInput = { type: 'storeNumber', value: storeNumber.trim() };
    void submitPin(digits, pendingAction, location);
  }, [storeNumber, digits, pendingAction, submitPin]);

  const enterDigit = useCallback(
    (digit: string) => {
      if (step !== 'keypad') return;
      setDigits((prev) => (prev.length < PIN_LENGTH ? prev + digit : prev));
    },
    [step],
  );

  const clearDigits = useCallback(() => {
    setDigits('');
    setErrorMessage(null);
  }, []);

  // Arm/Disarm submit directly now — no more auto-submit at the 4th digit.
  const chooseAction = useCallback(
    (action: VirtualTabletAction) => {
      if (step !== 'keypad' || digits.length !== PIN_LENGTH) return;
      setPendingAction(action);
      void resolveLocationAndSubmit(digits, action);
    },
    [step, digits, resolveLocationAndSubmit],
  );
  const submitArm = useCallback(() => chooseAction('arm'), [chooseAction]);
  const submitDisarm = useCallback(() => chooseAction('disarm'), [chooseAction]);

  return {
    step,
    storeNumber,
    digits,
    errorMessage,
    completedAction,
    locationFailureReason,
    armingSecondsRemaining,
    armingTotalSeconds,
    setStoreNumber,
    submitStoreNumber,
    retryLocation,
    enterDigit,
    clearDigits,
    submitArm,
    submitDisarm,
  };
}
